import KmxArrowUpRightCircleStroked from '@kmx/mui-icons/KmxArrowUpRightCircleStroked';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip, { ChipProps } from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Vehicle } from '../../../../api/types';
import { KmxContainer } from '../../../../components/KmxContainer';
import useAppState from '../../../../common/state/appState';

type ShippingChipProps = Pick<ChipProps, 'variant' | 'sx'>;

const ShippingChip = ({ variant, sx }: ShippingChipProps) => (
    <Chip
        color="success"
        variant={variant}
        size="small"
        icon={<KmxArrowUpRightCircleStroked fontSize="medium" />}
        label="Shipping"
        sx={sx}
    />
);

interface ICarHeroImageProps {
    stockNumber: number;
    productTitle: string;
}

const CarHeroImage = ({ stockNumber, productTitle }: ICarHeroImageProps) => {
    const imageLink = `https://img2.carmax.com/assets/${stockNumber}/hero.jpg?width=532&height=300`;
    const desktopImageLink = `https://img2.carmax.com/assets/${stockNumber}/hero.jpg?width=800&height=600`;

    return (
        <Box>
            <ShippingChip
                sx={{
                    display: { xs: 'inline-flex', sm: 'none' },
                    mt: 1,
                    ml: 1,
                    position: 'absolute',
                }}
                variant="filled"
            />
            <Link href={`/car/${stockNumber}`} title={productTitle} underline="none">
                <Box
                    sx={{
                        borderTopLeftRadius: { xs: 24 },
                        borderTopRightRadius: { xs: 24 },
                        borderRadius: { sm: 1 },
                        overflow: 'hidden',
                    }}
                >
                    <picture>
                        <source media="(min-width: 600px)" srcSet={desktopImageLink} />
                        <img
                            className="transfer-tile--hero-image"
                            src={imageLink}
                            alt={`Exterior view of a ${productTitle}`}
                            width="100%"
                            height="auto"
                            style={{ display: 'block' }}
                        />
                    </picture>
                </Box>
            </Link>
        </Box>
    );
};

interface ICarDetailsProps {
    vehicle: Vehicle;
    productTitle: string;
    basePrice: string;
    mileage: string;
}

const CarDetails = ({
    vehicle: { stockNumber, year, make, model, trim },
    productTitle,
    basePrice,
    mileage,
}: ICarDetailsProps) => (
    <Stack
        useFlexGap
        direction="column"
        spacing={2}
        sx={{
            px: { xs: 3, sm: 0 },
            pt: { xs: 0 },
            pb: { xs: 2, sm: 0 },
            justifyContent: { sm: 'center' },
            height: '100%',
        }}
    >
        <ShippingChip sx={{ width: 89, display: { xs: 'none', sm: 'inline-block' } }} variant="weak" />
        <Link color="textPrimary" href={`/car/${stockNumber}`} title={productTitle} underline="none">
            <Typography component="h5" sx={{ typography: { xs: 'h6', sm: 'h5' } }} noWrap>
                <Stack direction="column" spacing={{ xs: 0, sm: 0.5 }} sx={{ mb: 0.5 }}>
                    <Box component="span">
                        {year} {make}
                    </Box>
                    <Box component="span">
                        {model} {trim}
                    </Box>
                </Stack>
            </Typography>
            <Typography color="textSecondary" component="span">
                <Stack
                    direction="row"
                    divider={<Divider flexItem orientation="vertical" />}
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                >
                    <Box component="span">{basePrice}</Box>
                    <Box component="span">{mileage}</Box>
                </Stack>
            </Typography>
        </Link>
        <Button sx={{ width: { xs: '100%' }, maxWidth: { sm: 150 } }} variant="outlined" href="/hub/dashboard">
            Track your order
        </Button>
    </Stack>
);

const TransferStatus = () => {
    const transferStatus = useAppState((state) => state.transferStatus);

    if (!transferStatus || !transferStatus.vehicle || !transferStatus.status) {
        return <></>;
    }
    const { vehicle } = transferStatus;
    const productTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim ?? ''}`;
    const basePrice = `${vehicle.basePrice.toLocaleString()}*`;
    const mileage = `${Math.ceil(vehicle.mileage / 1000)}k mi`;

    return (
        <KmxContainer maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2 } }}>
            <Box
                sx={{
                    borderRadius: { xs: 2, sm: 2.5 },
                    backgroundImage: {
                        sm: 'url(/images/home/hero/homebase-tablet.webp)',
                        md: 'url(/images/home/hero/homebase-xl.webp)',
                    },
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    mt: 2,
                    mb: { xs: 5, sm: 0 },
                }}
            >
                <Box
                    sx={{
                        p: { xs: 0, sm: 5 },
                        px: { md: 6 },
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Paper
                        elevation={1}
                        sx={{
                            minWidth: 327,
                            maxWidth: 900,
                            width: { md: 900 },
                            borderRadius: 1.5,
                        }}
                        aria-label="Your Car Transfer Status : Shipping"
                    >
                        <Grid container columnSpacing={3} rowSpacing={2} sx={{ padding: { sm: 1 } }}>
                            <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                                <CarHeroImage productTitle={productTitle} stockNumber={vehicle.stockNumber} />
                            </Grid>
                            <Grid size="grow">
                                <CarDetails
                                    vehicle={vehicle}
                                    productTitle={productTitle}
                                    basePrice={basePrice}
                                    mileage={mileage}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            </Box>
        </KmxContainer>
    );
};

export default TransferStatus;
